import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Building2, MapPin, Search, Grid, List, Heart, Users, ArrowLeft,
  Globe, Mail, Loader2, ChevronRight
} from 'lucide-react';

export default function PublicInstituicoesPage() {
  const [instituicoes, setInstituicoes] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDistrito, setSelectedDistrito] = useState('Todos');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  useEffect(() => {
    loadData();
  }, [selectedDistrito]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load distritos
      const distResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`);
      if (distResponse.ok) {
        const distData = await distResponse.json();
        setDistritos(distData);
      }

      // Load institutions
      const params = new URLSearchParams();
      if (selectedDistrito !== 'Todos') params.append('distrito_id', selectedDistrito);
      
      const instResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/instituicoes?${params}`);
      if (instResponse.ok) {
        const instData = await instResponse.json();
        setInstituicoes(instData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInstituicoes = instituicoes.filter(inst =>
    inst.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.distrito_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by distrito for stats
  const distritoStats = instituicoes.reduce((acc, inst) => {
    const d = inst.distrito_nome || 'Outro';
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="public-instituicoes-page">
      {/* Header */}
      <div className="bg-[#0066CC] text-white">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/publico" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Portal
            </Link>
            <Link to="/login" className="text-white/80 hover:text-white text-sm">
              Área Reservada
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-[#0066CC] text-white pb-16 pt-8">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Instituições Parceiras
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Conheça as instituições que fazem parte da rede ENTRAJUDA e o trabalho que desenvolvem em prol da comunidade
          </p>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 mt-8">
            <div className="text-center">
              <p className="text-4xl font-bold">{instituicoes.length}</p>
              <p className="text-sm text-white/70">Instituições</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold">{Object.keys(distritoStats).length}</p>
              <p className="text-sm text-white/70">Distritos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="container mx-auto px-4 -mt-8">
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Pesquisar instituições..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedDistrito} onValueChange={setSelectedDistrito}>
                <SelectTrigger className="w-full md:w-[200px] h-12" data-testid="distrito-filter">
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Todos os distritos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Todos">Todos os distritos</SelectItem>
                  {distritos.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-1 border rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* District Pills */}
      <div className="container mx-auto px-4 mt-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(distritoStats).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([distrito, count]) => (
            <Badge
              key={distrito}
              variant="outline"
              className="cursor-pointer hover:bg-[#0066CC] hover:text-white transition-colors px-3 py-1"
              onClick={() => {
                const d = distritos.find(x => x.nome === distrito);
                setSelectedDistrito(d ? d.id : 'Todos');
              }}
            >
              {distrito} ({count})
            </Badge>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#0066CC]" />
          </div>
        ) : filteredInstituicoes.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nenhuma instituição encontrada</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInstituicoes.map((inst) => (
              <Card 
                key={inst.id} 
                className="group hover:shadow-lg transition-all hover:border-[#0066CC]/30 overflow-hidden"
                data-testid={`instituicao-card-${inst.id}`}
              >
                <div className="h-2 bg-gradient-to-r from-[#0066CC] to-[#00AA66]"></div>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#0066CC]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC]/20 transition-colors">
                      <Building2 className="w-7 h-7 text-[#0066CC]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 group-hover:text-[#0066CC] transition-colors line-clamp-2">
                        {inst.nome}
                      </h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {inst.distrito_nome || 'Portugal'}
                      </p>
                    </div>
                  </div>

                  {/* Type */}
                  {inst.tipo && (
                    <Badge variant="outline" className="mt-4">
                      {inst.tipo}
                    </Badge>
                  )}

                  {/* Valencia count */}
                  {inst.valencias_ativas > 0 && (
                    <div className="mt-4 pt-4 border-t flex items-center gap-2 text-sm text-slate-500">
                      <Heart className="w-4 h-4 text-pink-500" />
                      {inst.valencias_ativas} respostas sociais ativas
                    </div>
                  )}

                  {/* Contact */}
                  <div className="mt-4 flex gap-3">
                    {inst.website && (
                      <a 
                        href={inst.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-[#0066CC] transition-colors"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                    {inst.email && (
                      <a 
                        href={`mailto:${inst.email}`}
                        className="text-slate-400 hover:text-[#0066CC] transition-colors"
                      >
                        <Mail className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredInstituicoes.map((inst) => (
              <Card 
                key={inst.id} 
                className="hover:shadow-md transition-all hover:border-[#0066CC]/30"
                data-testid={`instituicao-row-${inst.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#0066CC]/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900">{inst.nome}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {inst.distrito_nome || 'Portugal'}
                        {inst.tipo && <> • <Badge variant="outline" className="text-xs">{inst.tipo}</Badge></>}
                      </p>
                    </div>
                    <div className="hidden md:flex items-center gap-4 text-slate-500">
                      {inst.valencias_ativas > 0 && (
                        <span className="flex items-center gap-1 text-sm">
                          <Heart className="w-4 h-4 text-pink-500" />
                          {inst.valencias_ativas}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Results count */}
        {!loading && (
          <div className="text-center mt-8 text-slate-500">
            Mostrando {filteredInstituicoes.length} de {instituicoes.length} instituições
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-slate-100 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            A sua instituição ainda não faz parte?
          </h2>
          <p className="text-slate-600 mb-6 max-w-xl mx-auto">
            Junte-se à rede ENTRAJUDA e beneficie do apoio e recursos disponíveis para instituições de solidariedade social
          </p>
          <Link to="/portal/registar">
            <Button size="lg" className="bg-[#0066CC] hover:bg-[#0052a3]">
              <Building2 className="w-5 h-5 mr-2" />
              Registar Instituição
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; {new Date().getFullYear()} ENTRAJUDA - Rede de Apoio Social
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/publico" className="text-slate-400 hover:text-white text-sm">Portal Público</Link>
            <Link to="/publico/dados-abertos" className="text-slate-400 hover:text-white text-sm">Dados Abertos</Link>
            <Link to="/publico/voluntariado" className="text-slate-400 hover:text-white text-sm">Voluntariado</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
